/* Rarely plotter card preview — renders a gift message EXACTLY as the pen
   plotter will draw it, using the real single-line font glyphs and the same
   layout math as the plotter pipeline (rarely-plotter/card.py + handwrite.py).

   Requires plotter-fonts.js (window.PLOTTER_FONTS) to be loaded first.

   API:
     RarelyCardPreview.fonts()                      -> [nickname, ...]
     RarelyCardPreview.render(text, font, align)    -> svg string or null
       align: 'centered' (default) | 'left'
   The SVG's viewBox is the message half of the physical card (5.8125in wide,
   4.125in tall at 96dpi), so it can be dropped into any card mockup. */
(function () {
  'use strict';

  var REPL = { '‘': "'", '’': "'", '“': '"', '”': '"',
               '–': '-', '—': '-', '…': '...' };
  var MAX_WRAP = 75; /* matches MODEL_MAX_CHARS */

  function cfg() { return window.PLOTTER_FONTS || null; }

  function sanitize(text, glyphs) {
    Object.keys(REPL).forEach(function (k) { text = text.split(k).join(REPL[k]); });
    var out = '';
    for (var i = 0; i < text.length; i++) {
      var c = text[i];
      if (c === ' ' || c === '\n' || glyphs[c]) out += c;
    }
    return out;
  }

  /* --- wrapping: ports auto_wrap_width + to_lines (incl. the widow rule:
     never a single word alone on a line, cascading pull-down) --- */
  /* Chars per line = wrapBase + wrapPerLine x (number of lines): a short
     note gets a sensible width from the start and every extra line widens
     the block a little. Solving n = base + per x total/n gives the closed
     form below. Ported from handwrite.py auto_wrap_width. */
  /* Progressive target: chars per line = wrapBase + wrapPerLine x lines,
     solved in closed form from the character count (handwrite.py
     auto_wrap_width). */
  function wrapTarget(total) {
    var C = cfg() || {};
    var base = C.wrapBase != null ? C.wrapBase : 12, per = C.wrapPerLine != null ? C.wrapPerLine : 5;
    var cap = C.wrapMax != null ? C.wrapMax : MAX_WRAP;
    var n = Math.round((base + Math.sqrt(base * base + 4 * per * total)) / 2);
    return Math.max(10, Math.min(MAX_WRAP, cap, n, total));
  }
  /* The chars-per-line that lets the block scale largest: every candidate
     width is typeset and measured with the font's real advances; among
     widths within wrapTie of the best scale, the one nearest the
     progressive target wins (ports handwrite.py choose_wrap exactly). */
  /* Size-cap multiplier for short notes (handwrite.py short_boost): up to
     1 + shortBoostMax at one line, easing to 1.0 by shortBoostLines lines.
     Font path only; Fools Gold keeps the neural model's own size. */
  function shortBoost(nLines, fontName) {
    var C = cfg() || {};
    if (fontName === 'Fools Gold') return 1;
    var mx = C.shortBoostMax != null ? C.shortBoostMax : 0.5, at = C.shortBoostLines != null ? C.shortBoostLines : 4;
    if (nLines >= at) return 1;
    return 1 + mx * (at - nLines) / (at - 1);
  }
  function chooseWrap(text, glyphs, upem, box, mlh, gap, tracking, fontName) {
    var C = cfg() || {};
    var dpi = C.dpi, tie = C.wrapTie != null ? C.wrapTie : 0.985;
    var total = text.split('\n').join(' ').length;
    var target = wrapTarget(total);
    var bw = box[2] * dpi, bh = box[3] * dpi, cap = mlh * dpi / (gap * upem);
    var top = Math.min(C.wrapMax != null ? C.wrapMax : MAX_WRAP, total);
    var results = {}, best = 0;
    for (var n = 10; n <= top; n++) {
      var lines = toLines(text, n), w = 0;
      lines.forEach(function (ln) {
        var lw = 0;
        for (var i = 0; i < ln.length; i++) { var g = glyphs[ln[i]]; lw += (g ? g[0] : upem * 0.3) + tracking * upem; }
        if (lw > w) w = lw;
      });
      var h = (lines.length - 1) * gap * upem + upem;
      var sc = Math.min(bw / w, bh / h, cap * shortBoost(lines.length, fontName));
      results[n] = sc; if (sc > best) best = sc;
    }
    if (!best) return Math.max(10, Math.min(MAX_WRAP, total));
    var pick = null, pd = Infinity;
    for (var k = 10; k <= top; k++) {
      if (results[k] < best * tie) continue;
      var d = Math.abs(k - target);
      if (d < pd || (d === pd && k < pick)) { pick = k; pd = d; }
    }
    return pick;
  }

  function wrapParagraph(words, width) {
    var lines = [], cur = '';
    words.forEach(function (w) {
      if (!cur) { cur = w; return; }
      if (cur.length + 1 + w.length <= width) cur += ' ' + w;
      else { lines.push(cur); cur = w; }
    });
    if (cur) lines.push(cur);
    return lines;
  }

  function toLines(text, wrap) {
    var lines = [];
    text.split('\n').forEach(function (para) {
      para = para.replace(/\s+$/, '');
      if (!para) { lines.push(''); return; }
      var wrapped = wrapParagraph(para.split(/\s+/), wrap);
      for (var pass = 0; pass < wrapped.length; pass++) {
        var changed = false;
        for (var i = wrapped.length - 1; i > 0; i--) {
          if (wrapped[i].split(' ').length === 1 && wrapped[i - 1].split(' ').length >= 2) {
            var prev = wrapped[i - 1].split(' ');
            wrapped[i] = prev.pop() + ' ' + wrapped[i];
            wrapped[i - 1] = prev.join(' ');
            changed = true;
          }
        }
        if (!changed) break;
      }
      lines = lines.concat(wrapped);
    });
    while (lines.length && !lines[lines.length - 1]) lines.pop();
    return lines;
  }

  function avgAdvance(glyphs, upem, text) {
    var sum = 0, n = 0;
    for (var i = 0; i < text.length; i++) {
      var g = glyphs[text[i]];
      if (g) { sum += g[0]; n++; }
    }
    return n ? sum / n / upem : 0.5;
  }

  function esc(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  /* Deterministic stand-in for the plotter's jitter engine (presets.json
     "jitter": per-line rotation and offsets, per-glyph baseline wobble and
     size variance). The pen draws its own random jitter every time, so this
     is representative, not exact; seeded from the text so a preview is
     stable while typing. Values: rotate_deg, line_y_in, x_in, baseline_in,
     scale_var (inches / degrees / fraction). */
  function rng(seed) {
    var t = seed >>> 0;
    return function () { t += 0x6D2B79F5; var r = Math.imul(t ^ (t >>> 15), 1 | t); r ^= r + Math.imul(r ^ (r >>> 7), 61 | r); return ((r ^ (r >>> 14)) >>> 0) / 4294967296; };
  }
  function jitterFor(styleName) {
    var C = cfg(); if (!C || !C.handStyles || !styleName) return null;
    for (var i = 0; i < C.handStyles.length; i++) if (C.handStyles[i].name === styleName) return C.handStyles[i].jitter || null;
    return null;
  }

  function render(text, fontName, align, handStyle) {
    var C = cfg();
    if (!C || !C.fonts[fontName]) return null;
    var F = C.fonts[fontName];
    var glyphs = F.glyphs, upem = F.upem, dpi = C.dpi, gap = F.lineGapEm || C.lineGapEm, tracking = F.trackingEm || 0;

    text = sanitize(String(text || ''), glyphs);
    if (!text.replace(/\s/g, '')) return null;

    var n = text.replace(/^\s+|\s+$/g, '').length;
    var box = n < C.compactUnder ? C.boxCompact : C.boxFull;
    var bx = box[0] * dpi, by = box[1] * dpi, bw = box[2] * dpi, bh = box[3] * dpi;

    var wrap = chooseWrap(text, glyphs, upem, box, F.mlh, gap, tracking, fontName);
    var lines = toLines(text, wrap);
    if (!lines.length) return null;

    /* typeset in font units, y-down; per-glyph placement + line ink bboxes */
    var placed = [], ink = null, lineBoxes = [];
    lines.forEach(function (line, row) {
      var baseline = (row + 1) * gap * upem;
      var x = 0, lb = null, items = [];
      for (var i = 0; i < line.length; i++) {
        var g = glyphs[line[i]];
        if (!g) { x += upem * 0.3 + tracking * upem; continue; }
        if (g[1]) {
          items.push([x, baseline, g[1]]);
          var b = [x + g[2], baseline - g[5], x + g[4], baseline - g[3]];
          lb = lb ? [Math.min(lb[0], b[0]), Math.min(lb[1], b[1]),
                     Math.max(lb[2], b[2]), Math.max(lb[3], b[3])] : b;
        }
        x += g[0] + tracking * upem;
      }
      placed.push(items);
      lineBoxes.push(lb);
      if (lb) ink = ink ? [Math.min(ink[0], lb[0]), Math.min(ink[1], lb[1]),
                          Math.max(ink[2], lb[2]), Math.max(ink[3], lb[3])] : lb;
    });
    if (!ink) return null;

    var inkW = ink[2] - ink[0], inkH = ink[3] - ink[1];
    var cap = F.mlh * dpi / (gap * upem) * shortBoost(lines.length, fontName);
    var s = Math.min(bw / inkW, bh / inkH, cap);
    var tx = bx + (bw - inkW * s) / 2 - ink[0] * s;
    var ty = by + (bh - inkH * s) / 2 - ink[1] * s;
    var axis = (ink[0] + ink[2]) / 2;

    var J = jitterFor(handStyle);
    var rand = J ? rng(text.length * 7919 + lines.length) : null;
    var parts = [], ext = null;
    placed.forEach(function (items, row) {
      if (!items.length) return;
      var dx = 0;
      var jrot = 0, jx = 0, jy = 0;
      if (J) {
        jrot = ((rand() * 2 - 1) * (J.rotate_deg || 0));
        jx = (rand() * 2 - 1) * (J.x_in || 0) * dpi;
        jy = (rand() * 2 - 1) * (J.line_y_in || 0) * dpi;
      }
      if (align !== 'left' && lineBoxes[row]) {
        dx = (axis - (lineBoxes[row][0] + lineBoxes[row][2]) / 2) * s;
      }
      var lb = lineBoxes[row];
      if (lb) {
        var e = [tx + dx + lb[0] * s, ty + lb[1] * s, tx + dx + lb[2] * s, ty + lb[3] * s];
        ext = ext ? [Math.min(ext[0], e[0]), Math.min(ext[1], e[1]),
                     Math.max(ext[2], e[2]), Math.max(ext[3], e[3])] : e;
      }
      var gl = items.map(function (it) {
        var gy = 0, gs = 1;
        if (J) {
          if (J.baseline_in) gy = (rand() * 2 - 1) * J.baseline_in * dpi / s;
          if (J.scale_var) gs = 1 + (rand() * 2 - 1) * J.scale_var;
        }
        return '<g transform="translate(' + it[0].toFixed(1) + ',' + (it[1] + gy).toFixed(1) +
               ') scale(' + gs.toFixed(3) + ',' + (-gs).toFixed(3) + ')"><path d="' + esc(it[2]) + '"/></g>';
      }).join('');
      var lb2 = lineBoxes[row];
      var cxm = lb2 ? (lb2[0] + lb2[2]) / 2 * s : 0, cym = lb2 ? (lb2[1] + lb2[3]) / 2 * s : 0;
      var rot = J && jrot ? ' rotate(' + jrot.toFixed(2) + ' ' + cxm.toFixed(1) + ' ' + cym.toFixed(1) + ')' : '';
      parts.push('<g transform="translate(' + (tx + dx + jx).toFixed(2) + ',' + (ty + jy).toFixed(2) + ')' + rot +
                 ' scale(' + s.toFixed(5) + ')">' + gl + '</g>');
    });

    /* the viewBox is the card's full message half — true dimensions, true
       margins: what you see is the physical card, 1:1 */
    var vb = [0, C.card[1] / 2 * dpi, C.card[0] * dpi, C.card[1] / 2 * dpi];
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="' +
           vb.map(function (v) { return v.toFixed(1); }).join(' ') +
           '" preserveAspectRatio="xMidYMid meet">' +
           '<g fill="none" stroke="#063233" stroke-linecap="round" stroke-linejoin="round"' +
           ' stroke-width="1.1" vector-effect="non-scaling-stroke">' +
           parts.join('').replace(/<path /g, '<path vector-effect="non-scaling-stroke" ') +
           '</g></svg>';
  }

  window.RarelyCardPreview = {
    fonts: function () { var C = cfg(); return C ? Object.keys(C.fonts) : []; },
    handStyles: function () { var C = cfg(); return (C && C.handStyles) ? C.handStyles.map(function (h) { return h.name; }) : []; },
    maxChars: function () { var C = cfg(); return C ? C.maxChars : 380; },
    render: render
  };
})();
